<template>
  <div class="ownership-validation">
    <div class="ownership-validation__header">
      <div class="ownership-validation__icon">
        <i class="fa-solid fa-shield-check" />
      </div>
      <div class="ownership-validation__heading">
        <h5>{{ t('shop.my_domains.ownership_validation.title') }}</h5>
        <p
          v-html="t('shop.my_domains.ownership_validation.intro', { domain: domain.domain })"
        />
      </div>
    </div>

    <!-- Email validation temporairement désactivé. Décommenter pour réactiver
         (ainsi que le bloc <div v-if="activeTab === 'email'"> plus bas et
         les états/handlers correspondants dans le <script>). -->
    <!--
    <div class="ownership-validation__tabs">
      <button
        type="button"
        class="ownership-validation__tab"
        :class="{ 'ownership-validation__tab--active': activeTab === 'email' }"
        @click="activeTab = 'email'"
      >
        <i class="fa-regular fa-envelope" />
        <span>{{ t('shop.my_domains.ownership_validation.tabs.email') }}</span>
      </button>
      <button
        type="button"
        class="ownership-validation__tab"
        :class="{ 'ownership-validation__tab--active': activeTab === 'dns' }"
        @click="activeTab = 'dns'"
      >
        <i class="fa-regular fa-server" />
        <span>{{ t('shop.my_domains.ownership_validation.tabs.dns') }}</span>
      </button>
    </div>

    <div
      v-if="activeTab === 'email'"
      class="ownership-validation__panel"
    >
      <p class="ownership-validation__description">
        {{ t('shop.my_domains.ownership_validation.email.description') }}
      </p>

      <div
        v-if="!emailSent"
        class="ownership-validation__email-form"
      >
        <label
          :for="`localPart-${domain.id_domain}`"
          class="ownership-validation__label"
        >
          {{ t('shop.my_domains.ownership_validation.email.localPartLabel') }}
        </label>
        <div class="ownership-validation__email-input">
          <InputText
            :id="`localPart-${domain.id_domain}`"
            v-model="localPart"
            :placeholder="t('shop.my_domains.ownership_validation.email.localPartPlaceholder')"
            :class="{ 'p-invalid': emailError }"
            @keyup.enter="handleSendEmail"
          />
          <span class="ownership-validation__email-suffix">@{{ domain.domain }}</span>
        </div>
        <small
          v-if="emailError"
          class="ownership-validation__error"
        >
          {{ emailError }}
        </small>
        <div class="ownership-validation__actions">
          <Button
            class="p-button p-button-success"
            :loading="sendingEmail"
            :disabled="!localPart.trim()"
            :label="t('shop.my_domains.ownership_validation.email.submit')"
            icon="fa-regular fa-paper-plane"
            @click="handleSendEmail"
          />
        </div>
      </div>

      <div
        v-else
        class="ownership-validation__success-banner"
      >
        <i class="fa-solid fa-circle-check" />
        <p
          v-html="t('shop.my_domains.ownership_validation.email.sent', { email: `${localPart}@${domain.domain}` })"
        />
      </div>
    </div>
    -->

    <div
      v-if="activeTab === 'dns'"
      class="ownership-validation__panel"
    >
      <p class="ownership-validation__description">
        {{ t('shop.my_domains.ownership_validation.dns.description') }}
      </p>

      <div class="ownership-validation__dns-record">
        <div class="ownership-validation__dns-cell">
          <span class="ownership-validation__dns-label">
            {{ t('shop.my_domains.ownership_validation.dns.recordTypeLabel') }}
          </span>
          <code>TXT</code>
        </div>
        <div class="ownership-validation__dns-cell">
          <span class="ownership-validation__dns-label">
            {{ t('shop.my_domains.ownership_validation.dns.recordHostLabel') }}
          </span>
          <code>{{ domain.domain }}</code>
        </div>
        <div class="ownership-validation__dns-cell ownership-validation__dns-cell--value">
          <span class="ownership-validation__dns-label">
            {{ t('shop.my_domains.ownership_validation.dns.recordValueLabel') }}
          </span>
          <div class="ownership-validation__dns-value-row">
            <code>{{ dnsRecordValue }}</code>
            <Button
              class="p-button-secondary ownership-validation__copy-btn"
              icon="far fa-clipboard"
              :label="t('copy')"
              severity="secondary"
              text
              @click="handleCopy"
            />
          </div>
        </div>
      </div>

      <small
        v-if="dnsError"
        class="ownership-validation__error"
      >
        {{ dnsError }}
      </small>

      <div class="ownership-validation__actions">
        <Button
          class="p-button p-button-success"
          :loading="verifyingDns"
          :label="t('shop.my_domains.ownership_validation.dns.verify')"
          icon="fa-solid fa-rotate"
          @click="handleVerifyDns"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
// import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { ShopsSendDomains } from '@/types/generated-types/graphql';
import { /* ValidateDomainByEmail, */ VerifyDomain } from '@/composables/shop/ShopsSendDomains';
import { showToastSuccess } from '@/helpers';
// import { UserState } from '@/composables/User';

export default defineComponent({
  name: 'DomainOwnershipValidation',

  components: {
    // InputText,
    Button,
  },

  props: {
    domain: {
      type: Object as PropType<ShopsSendDomains>,
      required: true,
    },
  },

  emits: ['validated'],

  setup(props, { emit }) {
    const { t } = useI18n();
    // Email validation désactivée temporairement — décommenter avec la
     // section template correspondante pour réactiver.
    const activeTab = ref<'email' | 'dns'>('dns');
    // const localPart = ref('');
    // const sendingEmail = ref(false);
    // const emailSent = ref(false);
    // const emailError = ref('');
    const verifyingDns = ref(false);
    const dnsError = ref('');

    const dnsRecordValue = computed(
      () => `shopimind-verification-key=${props.domain.dns_verification_key ?? ''}`,
    );

    // Email validation désactivée temporairement — décommenter pour réactiver.
    /*
    const handleSendEmail = async () => {
      const trimmed = localPart.value.trim();
      if (!trimmed) {
        return;
      }

      emailError.value = '';
      sendingEmail.value = true;
      try {
        const idShop = UserState.activeShop?.id ?? 0;
        const idDomain = Number(props.domain.id_domain ?? 0);
        const response = await ValidateDomainByEmail(idShop, idDomain, trimmed);

        if (response && (response as any).success) {
          emailSent.value = true;
          await showToastSuccess(t('shop.my_domains.ownership_validation.email.sent', {
            email: `${trimmed}@${props.domain.domain}`,
          }));
        } else {
          emailError.value = t('shop.my_domains.ownership_validation.email.errorSendFailed');
        }
      } catch (err) {
        emailError.value = t('shop.my_domains.ownership_validation.email.errorSendFailed');
      } finally {
        sendingEmail.value = false;
      }
    };
    */

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(dnsRecordValue.value);
        await showToastSuccess(t('copiedToClipboard'));
      } catch (err) {
        // ignore
      }
    };

    const handleVerifyDns = async () => {
      dnsError.value = '';
      verifyingDns.value = true;
      try {
        const idDomain = Number(props.domain.id_domain ?? 0);
        const { data, err } = await VerifyDomain(idDomain);

        if (err || !data) {
          dnsError.value = t('shop.my_domains.ownership_validation.dns.errorGeneric');
          return;
        }

        const raw = data.domain_verified as unknown;
        const verified = raw === true || raw === 1 || raw === '1';
        if (verified) {
          await showToastSuccess(t('shop.my_domains.ownership_validation.successMessage'));
          emit('validated', data);
        } else {
          dnsError.value = t('shop.my_domains.ownership_validation.dns.errorNotFound');
        }
      } catch (err) {
        dnsError.value = t('shop.my_domains.ownership_validation.dns.errorGeneric');
      } finally {
        verifyingDns.value = false;
      }
    };

    return {
      t,
      activeTab,
      // localPart,
      // sendingEmail,
      // emailSent,
      // emailError,
      verifyingDns,
      dnsError,
      dnsRecordValue,
      // handleSendEmail,
      handleCopy,
      handleVerifyDns,
    };
  },
});
</script>

<style scoped lang="scss">
.ownership-validation {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(147, 197, 253, 0.12), rgba(148, 200, 64, 0.1));
  border-radius: 20px;
  border: 1px solid rgba(27, 42, 75, 0.08);
}

.ownership-validation__header {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.ownership-validation__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(148, 200, 64, 0.2);
  color: #4b7b06;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.ownership-validation__heading {
  flex: 1;

  h5 {
    margin: 0 0 0.35rem 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: #13233d;
  }

  p {
    margin: 0;
    color: #2e3e5c;
    font-size: 0.9rem;
    line-height: 1.5;
  }
}

.ownership-validation__tabs {
  display: flex;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 12px;
  padding: 0.35rem;
}

.ownership-validation__tab {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  border: 0;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  color: #4a5b78;
  font-weight: 500;
  font-size: 0.9rem;
  transition: background 0.15s, color 0.15s;

  &:hover:not(.ownership-validation__tab--active) {
    background: rgba(255, 255, 255, 0.7);
  }

  i {
    font-size: 0.95rem;
  }
}

.ownership-validation__tab--active {
  background: #ffffff;
  color: #13233d;
  box-shadow: 0 2px 8px rgba(27, 42, 75, 0.08);
}

.ownership-validation__panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: #ffffff;
  border-radius: 14px;
  padding: 1.25rem;
  border: 1px solid rgba(27, 42, 75, 0.06);
}

.ownership-validation__description {
  margin: 0;
  font-size: 0.9rem;
  color: #4a5b78;
  line-height: 1.5;
}

.ownership-validation__email-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.ownership-validation__label {
  font-size: 0.85rem;
  color: #2e3e5c;
  font-weight: 500;
}

.ownership-validation__email-input {
  display: flex;
  align-items: center;
  gap: 0;
  background: #f7f9fc;
  border: 1px solid rgba(27, 42, 75, 0.12);
  border-radius: 10px;
  overflow: hidden;

  :deep(input.p-inputtext) {
    border: 0;
    background: transparent;
    flex: 1;
    box-shadow: none;
  }
}

.ownership-validation__email-suffix {
  padding: 0 0.85rem;
  color: #4a5b78;
  font-size: 0.9rem;
  border-left: 1px solid rgba(27, 42, 75, 0.1);
  height: 100%;
  display: flex;
  align-items: center;
}

.ownership-validation__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.ownership-validation__error {
  color: #b71c1c;
  font-size: 0.85rem;
}

.ownership-validation__success-banner {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 1rem 1.1rem;
  background: rgba(148, 200, 64, 0.15);
  border-radius: 12px;
  color: #4b7b06;

  i {
    font-size: 1.1rem;
    margin-top: 0.15rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.5;
    color: #2e3e5c;

    :deep(b) {
      color: #4b7b06;
    }
  }
}

.ownership-validation__dns-record {
  display: grid;
  grid-template-columns: minmax(80px, auto) minmax(120px, auto) 1fr;
  gap: 1rem;
  background: #f7f9fc;
  border-radius: 12px;
  padding: 1rem;
  border: 1px dashed rgba(27, 42, 75, 0.15);

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
}

.ownership-validation__dns-cell {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  code {
    background: #ffffff;
    padding: 0.4rem 0.65rem;
    border-radius: 8px;
    font-size: 0.85rem;
    color: #13233d;
    border: 1px solid rgba(27, 42, 75, 0.08);
    word-break: break-all;
  }
}

.ownership-validation__dns-cell--value {
  min-width: 0;
}

.ownership-validation__dns-value-row {
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  min-width: 0;

  code {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
  }
}

.ownership-validation__copy-btn {
  flex-shrink: 0;
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
  white-space: nowrap;
}


.ownership-validation__dns-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #4a5b78;
  font-weight: 600;
}
</style>
